import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { PageHeader, Spinner, EmptyState, Card, Modal } from "@/components/common";
import { toast } from "sonner";
import { UserSearch, MapPin, Search, Send, UserRound } from "lucide-react";

export default function FindPlayers() {
  const navigate = useNavigate();
  const [players, setPlayers] = useState(null);
  const [sports, setSports] = useState([]);
  const [sport, setSport] = useState("");
  const [city, setCity] = useState("");
  const [openOnly, setOpenOnly] = useState(true);
  const [myTeams, setMyTeams] = useState([]);
  const [invitee, setInvitee] = useState(null); // athlete being invited

  const load = () => {
    const p = new URLSearchParams();
    if (sport) p.set("sport", sport);
    if (city) p.set("city", city);
    if (openOnly) p.set("open_to_join", "true");
    api.get(`/players?${p.toString()}`).then((r) => setPlayers(r.data)).catch(() => setPlayers([]));
  };
  useEffect(() => {
    api.get("/public/filters").then((r) => setSports(r.data?.sports || [])).catch(() => {});
    api.get("/me/teams").then((r) => setMyTeams((r.data || []).filter((t) => t.my_role === "captain"))).catch(() => {});
  }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [sport, city, openOnly]);

  const invite = async (tid) => {
    try { await api.post(`/teams/${tid}/invite-player`, { athlete_id: invitee.id }); toast.success(`Invited ${invitee.display_name}`); setInvitee(null); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="animate-fadeUp">
      <PageHeader title="Find players" subtitle="Discover players open to join and invite them to your team" />

      <Card className="mb-5">
        <div className="grid gap-3 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
          <div>
            <label className="mb-1.5 block text-sm font-medium">Sport</label>
            <select data-testid="players-sport" value={sport} onChange={(e) => setSport(e.target.value)} className="w-full rounded-lg border border-input bg-background px-3 py-2.5 text-sm outline-none focus:border-primary">
              <option value="">All sports</option>
              {sports.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium">City</label>
            <div className="relative"><Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
              <input data-testid="players-city" value={city} onChange={(e) => setCity(e.target.value)} placeholder="Any city" className="w-full rounded-lg border border-input bg-background py-2.5 pl-9 pr-3 text-sm outline-none focus:border-primary" />
            </div>
          </div>
          <button data-testid="players-open-filter" onClick={() => setOpenOnly((v) => !v)}
            className={`inline-flex h-[42px] items-center gap-2 rounded-lg border px-4 text-sm font-medium transition-colors ${openOnly ? "border-emerald-500/40 bg-emerald-50 text-emerald-700" : "border-border bg-background text-muted-foreground"}`}>
            <span className={`h-2 w-2 rounded-full ${openOnly ? "bg-emerald-500" : "bg-muted-foreground"}`} /> Open to join only
          </button>
        </div>
      </Card>

      {!players ? <Spinner /> : players.length === 0 ? (
        <EmptyState icon={UserSearch} title="No players found" text="Try a different sport or city, or turn off the open-to-join filter." />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {players.map((p) => (
            <Card key={p.id} className="flex flex-col">
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 font-display text-lg font-bold text-primary">{(p.display_name || "?")[0].toUpperCase()}</div>
                <div className="min-w-0">
                  <p className="truncate font-display text-lg font-semibold">{p.display_name}</p>
                  <p className="text-xs capitalize text-muted-foreground">{p.level || "amateur"}</p>
                </div>
                {p.open_to_join && <span className="ml-auto rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700">Open</span>}
              </div>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {(p.sports?.length ? p.sports : (p.sport ? [p.sport] : [])).map((s) => <span key={s} className="rounded-full bg-secondary px-2 py-0.5 text-xs capitalize">{s}</span>)}
              </div>
              {p.city && <p className="mt-2 flex items-center gap-1 text-xs text-muted-foreground"><MapPin className="h-3 w-3" /> {p.city}</p>}
              {p.bio && <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">{p.bio}</p>}
              <button data-testid={`invite-player-${p.id}`} onClick={() => setInvitee(p)} disabled={myTeams.length === 0}
                title={myTeams.length === 0 ? "Create a team first" : ""}
                className="mt-auto flex items-center justify-center gap-2 rounded-lg bg-primary py-2.5 text-sm font-semibold text-primary-foreground transition-colors hover:bg-[#FF8533] disabled:opacity-50">
                <Send className="h-4 w-4" /> Invite to team
              </button>
            </Card>
          ))}
        </div>
      )}

      <Modal open={!!invitee} onClose={() => setInvitee(null)} title={`Invite ${invitee?.display_name || ""}`}>
        {myTeams.length === 0 ? (
          <div className="text-center">
            <p className="text-sm text-muted-foreground">You need to captain a team first.</p>
            <button onClick={() => navigate("/app/teams")} className="mt-4 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground">Create a team</button>
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">Choose which of your teams to invite them to:</p>
            {myTeams.map((t) => (
              <button key={t.id} data-testid={`invite-to-team-${t.id}`} onClick={() => invite(t.id)}
                className="flex w-full items-center justify-between rounded-lg border border-border bg-background p-3 text-left transition-colors hover:border-primary/50">
                <span><span className="font-semibold">{t.name}</span> <span className="text-xs text-muted-foreground">· {t.sport}{t.city ? ` · ${t.city}` : ""}</span></span>
                <UserRound className="h-4 w-4 text-primary" />
              </button>
            ))}
          </div>
        )}
      </Modal>
    </div>
  );
}
